import React from 'react';
import { Button } from 'antd';
import { useTranslation } from 'react-i18next';
import logo from '../../assets/branding/android-chrome-192x192.png';
import qrCode from '../../assets/qrcode.jpg';

// Constants
const INSTAGRAM_HANDLE = 'my_.express';
const WHATSAPP_NUMBER = '0783 083 3103';

function getDirection(i18n: any): 'rtl' | 'ltr' {
  const rtlLangs = ['ar', 'ckb', 'ku'];
  return rtlLangs.includes(i18n.language) ? 'rtl' : 'ltr';
}

function getTotalQty(order: any) {
  let qty = Number(order.qty) || 0;
  if (Array.isArray(order.subOrders)) {
    qty += order.subOrders.reduce((sum: any, s: any) => sum + (Number(s.pieces) || 0), 0);
  }
  return qty;
}

function getGrandTotal(order: any) {
  const total = Number(order.totalPrice || 0);
  const delivery = Number(order.deliveryCost || 0);
  return total + delivery;
}

function renderInstagramQR() {
  const size = 88;
  return `<img src="${qrCode}" alt="Instagram QR" style="width:${size}px;height:${size}px;border-radius:8px;margin-top:7px;" />`;
}

function renderInvoiceHTML(order: any, t: any, dir: 'rtl' | 'ltr'): string {
  const customer = order.customerId || {};
  const customerName = customer.name || order.customerName || '-';
  const customerAddress = customer.address || order.address || '-';
  const customerPhone1 = customer.phone1 || order.phone1 || '-';
  const customerPhone2 = customer.phone2 || order.phone2 || '-';

  const totalQty = getTotalQty(order);
  const grandTotal = getGrandTotal(order);

  return `
    <div class="invoice-outer">
      <div class="invoice-container" dir="${dir}">
        <div class="invoice-header">
          <img src="${logo}" alt="Logo" class="invoice-logo" style="width:44px;height:44px;border-radius:8px;margin-top:7px;"/>
          <div class="invoice-title-block">
            <h2 class="invoice-title">${t('order_invoice')}</h2>
            <div class="invoice-number">${order.invoiceNumber ? 'NO. ' + order.invoiceNumber : ''}</div>
          </div>
        </div>
        <hr />
        <div class="invoice-info">
          <div><b>${t('customer_name')}:</b> ${customerName}</div>
          <div><b>${t('customer_address')}:</b> ${customerAddress}</div>
          <div><b>${t('customer_code')}:</b> ${order.customerCode || '-'}</div>
          <div><b>${t('phone1')}:</b> ${customerPhone1}</div>
          <div><b>${t('phone2')}:</b> ${customerPhone2}</div>
        </div>
        <table class="invoice-table">
          <thead>
            <tr>
              <th>${t('total_qty')}</th>
              <th>${t('total_price')}</th>
              <th>${t('delivery_cost')}</th>
              <th>${t('grand_total')}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>${totalQty}</td>
              <td>${Number(order.totalPrice).toLocaleString()}</td>
              <td>${Number(order.deliveryCost).toLocaleString()}</td>
              <td style="font-weight:bold;color:#d30b1a">${grandTotal.toLocaleString()} IQD</td>
            </tr>
          </tbody>
        </table>
        <div class="invoice-grand">${t('grand_total')}: <b>${grandTotal.toLocaleString()} IQD</b></div>
        <div class="invoice-footer">
          <div style="display:flex;justify-content:space-between;align-items:center;gap:18px;margin-top:18px;">
            <div>
              <div style="margin-bottom:2px;">
                ${t('whatsapp_viber')}: <b>${WHATSAPP_NUMBER}</b>
              </div>
              <div>
                Instagram: <b>@${INSTAGRAM_HANDLE}</b>
              </div>
              <div style="margin-top:2px;font-size:0.98rem;color:#656">${t('scan_qr_instagram')}</div>
            </div>
            <div>
              ${renderInstagramQR()}
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}

// --- NOTE: Noto Naskh Arabic is loaded for Arabic/Kurdish support! ---
const getPrintStyles = (): string => `
  @media print {
    @page { size: A6 portrait; margin: 0; }
    html, body { background: #fff !important; margin: 0 !important; }
    .invoice-outer { background: #fff !important; min-height: 0 !important; }
    .invoice-container { border-radius: 0 !important; box-shadow: none !important; border: none !important; }
  }
  body { background: #f6f6f6; }
  .invoice-outer {
    background: #fff;
    margin: 0 auto;
    padding: 0;
    width: 100%;
    min-height: unset;
    display: block;
  }
  .invoice-container {
    background: #fff;
    border-radius: 10px;
    box-shadow: 0 4px 24px #1640a110;
    padding: 10px 10px;
    width: 390px;
    max-width: 100vw;
    margin: 0 auto;
    font-family: 'Noto Naskh Arabic', 'Segoe UI', Arial, Tahoma, sans-serif !important;
    color: #23272f;
    direction: inherit;
    border: 1px solid #ececec;
    font-size: 12px;
  }
  .invoice-header {
    display: flex;
    align-items: center;
    margin-bottom: 10px;
    gap: 10px;
  }
  .invoice-logo {
    width: 44px;
    height: 44px;
    object-fit: contain;
    border-radius: 4px;
    background: none;
    box-shadow: none;
  }
  .invoice-title-block {
    flex: 1;
    text-align: left;
  }
  [dir="rtl"] .invoice-title-block { text-align: right; }
  .invoice-title {
    margin: 0 0 3px 0;
    font-size: 1.05rem;
    font-weight: 600;
    color: #1640a1;
    letter-spacing: 0.04em;
    font-family: 'Noto Naskh Arabic', 'Segoe UI', Arial, Tahoma, sans-serif !important;
  }
  .invoice-number {
    font-size: 0.98rem;
    color: #d30b1a;
    font-weight: bold;
    margin-top: 2px;
  }
  .invoice-info {
    margin-bottom: 12px;
    font-size: 0.98rem;
    line-height: 1.45;
    background: #f9f9fe;
    border-radius: 5px;
    padding: 7px 10px;
    border: 1px solid #eee;
  }
  .invoice-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 7px;
    margin-bottom: 13px;
    font-size: 0.95rem;
  }
  .invoice-table th {
    background: #f3f6fd;
    font-weight: 600;
    color: #1b3257;
    border-bottom: 2px solid #e5eafe;
    padding: 5px 3px;
    font-size: 0.93rem;
  }
  .invoice-table td {
    border: 1px solid #ececec;
    padding: 5px 3px;
    text-align: center;
    background: #fff;
    font-size: 0.93rem;
  }
  .invoice-grand {
    font-size: 1.01rem;
    font-weight: bold;
    margin: 10px 0 0 0;
    color: #18387a;
    text-align: left;
  }
  .invoice-footer {
    margin-top: 20px;
    color: #888;
    font-size: 0.93rem;
    text-align: center;
    border-top: 1px solid #e5eafe;
    padding-top: 7px;
  }
`;

interface PrintInvoiceButtonProps {
  order: any;
}
const PrintInvoiceButton: React.FC<PrintInvoiceButtonProps> = ({ order }) => {
  const { t, i18n } = useTranslation();
  const dir = getDirection(i18n);

  const handlePrint = () => {
    const win = window.open('', '_blank', 'width=900,height=650');
    if (!win) return;
    win.document.write(`
      <!DOCTYPE html>
      <html dir="${dir}">
        <head>
          <meta charset="UTF-8"/>
          <title>${t('print_invoice')}</title>
          <link href="https://fonts.googleapis.com/css2?family=Noto+Naskh+Arabic:wght@400;700&display=swap" rel="stylesheet">
          <style>${getPrintStyles()}</style>
        </head>
        <body>
          ${renderInvoiceHTML(order, t, dir)}
          <script>
            window.onload = function() {
              window.print();
              setTimeout(function() { window.close(); }, 200);
            }
          </script>
        </body>
      </html>
    `);
    win.document.close();
  };

  return (
    <Button onClick={handlePrint} type="primary">
      {t('print_invoice')}
    </Button>
  );
};

export default PrintInvoiceButton;
export { renderInvoiceHTML, getPrintStyles };
